import React from "react";
import { useTranslation } from "react-i18next";

/**
 * V2 styled button component for authentication forms
 *
 * @param {Object} props
 * @param {'primary' | 'secondary'} props.variant - Button variant
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.loading - Loading state
 * @param {React.ReactNode} props.children - Button content
 */
export default function V2AuthButton({
  variant = "primary",
  disabled = false,
  loading = false,
  children,
  className = "",
  type = "button",
  ...props
}) {
  const { t } = useTranslation();
  const isDisabled = disabled || loading;

  const baseStyles = `
    w-full py-3 rounded-[10px] text-sm font-semibold
    transition-all duration-150 outline-none
  `;

  const primaryStyles = isDisabled
    ? "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
    : "text-white shadow-[0_2px_8px_rgba(224,122,58,0.3)] hover:shadow-[0_4px_12px_rgba(224,122,58,0.4)]";

  const secondaryStyles = isDisabled
    ? "bg-[#F3F4F6] text-[#9CA3AF] border border-[#E5E7EB] cursor-not-allowed"
    : "bg-white text-[#374151] border border-[#E5E7EB] hover:bg-[#F9FAFB]";

  const variantStyles = variant === "primary" ? primaryStyles : secondaryStyles;

  return (
    <button
      type={type}
      disabled={isDisabled}
      className={`${baseStyles} ${variantStyles} ${className}`}
      style={
        variant === "primary" && !isDisabled
          ? { background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)" }
          : undefined
      }
      {...props}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          {t("auth.common.loading")}
        </span>
      ) : (
        children
      )}
    </button>
  );
}
